// Practice Test Reply Bank
// Duolingo-style energy.
// EN NL AR translations.
// Three types. Relevant emojis.

export type ReplyType =
  'emoji' | 'motivation' | 'correct-answer'

export interface Reply {
  type: ReplyType
  text: string
}

export type Lang = 'en' | 'nl' | 'ar'

// ═══════════════════════
// EMOJI POOLS
// Same for all languages
// ═══════════════════════

export const CORRECT_EMOJI_POOL = [
  '✅', '🎯', '🏆', '⭐',
  '🎉', '👌', '💪', '🧠',
  '🌟', '👍', '🎓', '🚀',
  '💡', '🔥', '😎', '✨',
  '👏', '🙌', '📌', '💥'
]

export const INCORRECT_EMOJI_POOL = [
  '📖', '🤔', '👀', '📝',
  '🔍', '🧩', '🔑', '🌱',
  '📚', '💭', '🔄', '⏳',
  '🧠', '🎓', '📐', '💡',
  '🗺️', '⚡', '🔎', '📌'
]

// ═══════════════════════
// ENGLISH
// ═══════════════════════

const CORRECT_MOTIVATION_EN = [
  'Legendary!',
  "You're on fire!",
  'Amazing!',
  'Incredible!',
  'Unstoppable!',
  'You crushed it!',
  'That is the one!',
  'Yes! Keep going!',
  'On a roll!',
  'Sharp as ever!',
  'Killing it!',
  'Superb!',
  'Flawless!',
  'Too easy for you!',
  'Textbook!',
  'Dialled in!',
  'Clean!',
  "That's what I'm talking about!",
  'Absolutely!',
  'Nailed it!',
]

const INCORRECT_MOTIVATION_EN = [
  'Almost! Keep going.',
  'Not this time. You will get it.',
  'Oops! Easy to mix up.',
  'Nearly! Remember this one.',
  'That one trips people up.',
  'Easy to mix up. Keep going.',
  "You'll get this next time.",
  "Don't sweat it. Keep moving.",
  'One to remember.',
  'Stay with it.',
  'Progress not perfection.',
  'One step at a time.',
  'You are getting there.',
  'Keep your head up.',
  'Every question makes you better.',
  'This one will not catch you again.',
  'Shake it off. Next question.',
  'Building knowledge takes time.',
  'You showed up. That counts.',
  'Onwards.',
]

const CORRECT_ANSWER_TEMPLATES_EN = [
  (a: string) =>
    `The correct answer is: ${a}`,
  (a: string) =>
    `Remember this rule: ${a}`,
  (a: string) =>
    `The right answer was: ${a}`,
  (a: string) =>
    `Next time remember: ${a}`,
  (a: string) =>
    `The answer was: ${a}`,
  (a: string) =>
    `Worth knowing: ${a}`,
  (a: string) =>
    `For this question: ${a}`,
  (a: string) =>
    `Pin this one: ${a}`,
]

const STREAK_MESSAGES_EN: Record<
  number, string
> = {
  3: '3 in a row!',
  5: "You're on fire!",
  8: 'Unstoppable!',
  10: 'Legendary streak!',
}

// ═══════════════════════
// DUTCH
// ═══════════════════════

const CORRECT_MOTIVATION_NL = [
  'Geweldig!',
  'Je bent on fire!',
  'Verbazingwekkend!',
  'Ongelooflijk!',
  'Niet te stoppen!',
  'Je hebt het geknald!',
  'Dat is hem!',
  'Ja! Ga door!',
  'Lekker bezig!',
  'Scherp als altijd!',
  'Je gaat tekeer!',
  'Schitterend!',
  'Vlekkeloos!',
  'Te makkelijk voor jou!',
  'Schoolvoorbeeld!',
  'Helemaal ingesteld!',
  'Strak!',
  'Dat is wat ik bedoel!',
  'Absoluut!',
  'Precies goed!',
]

const INCORRECT_MOTIVATION_NL = [
  'Bijna! Ga door.',
  'Niet deze keer. Je krijgt het.',
  'Oeps! Makkelijk te verwarren.',
  'Bijna! Onthoud deze.',
  'Die pakt veel mensen.',
  'Makkelijk te verwarren. Ga door.',
  'Volgende keer haal je het.',
  'Niet zweten. Blijf bewegen.',
  'Eentje om te onthouden.',
  'Blijf erbij.',
  'Vooruitgang niet perfectie.',
  'Stap voor stap.',
  'Je komt er.',
  'Hoofd omhoog.',
  'Elke vraag maakt je beter.',
  'Die pakt je niet meer.',
  'Schud het af. Volgende vraag.',
  'Kennis opbouwen kost tijd.',
  'Je bent er. Dat telt.',
  'Voorwaarts.',
]

const CORRECT_ANSWER_TEMPLATES_NL = [
  (a: string) =>
    `Het juiste antwoord is: ${a}`,
  (a: string) =>
    `Onthoud deze regel: ${a}`,
  (a: string) =>
    `Het goede antwoord was: ${a}`,
  (a: string) =>
    `Onthoud volgende keer: ${a}`,
  (a: string) =>
    `Het antwoord was: ${a}`,
  (a: string) =>
    `Goed om te weten: ${a}`,
  (a: string) =>
    `Voor deze vraag: ${a}`,
  (a: string) =>
    `Zet deze vast: ${a}`,
]

const STREAK_MESSAGES_NL: Record<
  number, string
> = {
  3: '3 op een rij!',
  5: 'Je bent on fire!',
  8: 'Niet te stoppen!',
  10: 'Legendarische reeks!',
}

// ═══════════════════════
// ARABIC
// ═══════════════════════

const CORRECT_MOTIVATION_AR = [
  '!أسطوري',
  '!أنت في قمة تألقك',
  '!رائع',
  '!لا يصدق',
  '!لا يمكن إيقافك',
  '!أبليت بلاءً حسناً',
  '!هذا هو',
  '!نعم! واصل',
  '!في تقدم مستمر',
  '!حاد كالمعتاد',
  '!أداء متميز',
  '!ممتاز',
  '!بلا أخطاء',
  '!سهل جداً عليك',
  '!من الكتاب مباشرة',
  '!في الوضع الصحيح',
  '!نظيف',
  '!هذا ما أتحدث عنه',
  '!بالتأكيد',
  '!أصبت في الصميم',
]

const INCORRECT_MOTIVATION_AR = [
  'تقريباً! واصل.',
  'ليس هذه المرة. ستحصل عليها.',
  'أوه! سهل الخلط.',
  'تقريباً! تذكر هذه.',
  'هذه تخدع كثيرين.',
  'سهل الخلط. واصل.',
  'ستحصل عليها في المرة القادمة.',
  'لا تقلق. استمر في التقدم.',
  'واحدة للتذكر.',
  'ثابر.',
  'التقدم لا الكمال.',
  'خطوة بخطوة.',
  'أنت في طريقك.',
  'ارفع رأسك.',
  'كل سؤال يجعلك أفضل.',
  'هذه لن تخدعك مرة أخرى.',
  'تجاوزها. السؤال التالي.',
  'بناء المعرفة يستغرق وقتاً.',
  'لقد حضرت. هذا يحسب.',
  'إلى الأمام.',
]

const CORRECT_ANSWER_TEMPLATES_AR = [
  (a: string) =>
    `الإجابة الصحيحة هي: ${a}`,
  (a: string) =>
    `تذكر هذه القاعدة: ${a}`,
  (a: string) =>
    `كانت الإجابة الصحيحة: ${a}`,
  (a: string) =>
    `تذكر في المرة القادمة: ${a}`,
  (a: string) =>
    `كانت الإجابة: ${a}`,
  (a: string) =>
    `يستحق المعرفة: ${a}`,
  (a: string) =>
    `لهذا السؤال: ${a}`,
  (a: string) =>
    `ثبّت هذه: ${a}`,
]

const STREAK_MESSAGES_AR: Record<
  number, string
> = {
  3: '3 على التوالي!',
  5: 'أنت في قمة تألقك!',
  8: 'لا يمكن إيقافك!',
  10: 'سلسلة أسطورية!',
}

// ═══════════════════════
// HELPERS
// ═══════════════════════

function pickRandom<T>(arr: T[]): T {
  return arr[
    Math.floor(Math.random() * arr.length)
  ]
}

function getMotivationPool(
  lang: Lang,
  correct: boolean
): string[] {
  if (correct) {
    if (lang === 'nl')
      return CORRECT_MOTIVATION_NL
    if (lang === 'ar')
      return CORRECT_MOTIVATION_AR
    return CORRECT_MOTIVATION_EN
  }
  if (lang === 'nl')
    return INCORRECT_MOTIVATION_NL
  if (lang === 'ar')
    return INCORRECT_MOTIVATION_AR
  return INCORRECT_MOTIVATION_EN
}

function getAnswerTemplates(
  lang: Lang
): ((a: string) => string)[] {
  if (lang === 'nl')
    return CORRECT_ANSWER_TEMPLATES_NL
  if (lang === 'ar')
    return CORRECT_ANSWER_TEMPLATES_AR
  return CORRECT_ANSWER_TEMPLATES_EN
}

export function getStreakMessage(
  streak: number,
  lang: Lang
): string | null {
  const messages =
    lang === 'nl' ? STREAK_MESSAGES_NL :
    lang === 'ar' ? STREAK_MESSAGES_AR :
    STREAK_MESSAGES_EN
  return messages[streak] ?? null
}

// ═══════════════════════
// PUBLIC API
// ═══════════════════════

export function pickCorrectReply(
  lang: Lang = 'en'
): Reply {
  const useEmoji = Math.random() < 0.5
  if (useEmoji) {
    return {
      type: 'emoji',
      text: pickRandom(CORRECT_EMOJI_POOL)
    }
  }
  return {
    type: 'motivation',
    text: pickRandom(
      getMotivationPool(lang, true)
    )
  }
}

export function pickIncorrectReply(
  correctAnswerText: string,
  lang: Lang = 'en'
): Reply {
  const roll = Math.random()
  if (roll < 0.20) {
    return {
      type: 'emoji',
      text: pickRandom(INCORRECT_EMOJI_POOL)
    }
  }
  if (roll < 0.70) {
    const template = pickRandom(
      getAnswerTemplates(lang)
    )
    return {
      type: 'correct-answer',
      text: template(correctAnswerText)
    }
  }
  return {
    type: 'motivation',
    text: pickRandom(
      getMotivationPool(lang, false)
    )
  }
}
